import React from 'react';
import { Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { CheckOutlined, CloseOutlined, RollbackOutlined } from '@ant-design/icons';

/**
 * 订单状态节点定义
 * 根据业务需求定义订单全流程状态节点
 */
export const ORDER_STATUS_NODES = [
  { key: 'ordersAccepted', label: 'order.statusFlow.ordersAccepted', timeField: 'acceptTime' },
  { key: 'readyToPick', label: 'order.statusFlow.readyToPick', timeField: 'readyToPickTime' },
  { key: 'startPicking', label: 'order.statusFlow.startPicking', timeField: 'startPickingTime' },
  { key: 'pickingCompleted', label: 'order.statusFlow.pickingCompleted', timeField: 'sortingCompleteTime' },
  { key: 'outboundCompleted', label: 'order.statusFlow.outboundCompleted', timeField: 'outboundCompletedTime' },
] as const;

/**
 * 订单状态节点数据接口
 */
export interface OrderStatusNodeData {
  key: string;
  label: string;
  time?: string | number | null;
  completed: boolean;
  isCurrent: boolean;
  isCancelled?: boolean;
}

/**
 * 订单状态流程组件属性
 */
interface OrderStatusFlowProps {
  /**
   * 当前激活的状态节点索引
   */
  current?: number;
  /**
   * 状态节点数据映射
   * key: 状态节点key, value: { time: 时间戳, completed: 是否完成 }
   */
  statusData?: Record<string, { time?: string | number | null; completed?: boolean }>;
  /** 是否已取消 */
  isCancelled?: boolean;
  /** 取消时间 */
  cancelTime?: string | number | null;
  /** 是否已退货 */
  isReturned?: boolean;
  /** 退货时间 */
  returnTime?: string | number | null;
}

/**
 * 订单状态流程组件
 * 展示订单从接单到完成的完整流程节点
 */
const OrderStatusFlow: React.FC<OrderStatusFlowProps> = ({
  current = 0,
  statusData = {},
  isCancelled = false,
  cancelTime,
  isReturned = false,
  returnTime,
}) => {
  const { t } = useTranslation();

  /**
   * 构建状态节点数据
   * 显示常规流程节点(0-4)
   */
  const buildStatusNodes = (): OrderStatusNodeData[] => {
    const nodes: OrderStatusNodeData[] = [];
    
    // 常规流程节点 (0-4)
    const normalNodes = ORDER_STATUS_NODES.slice(0, 5);
    
    // 确定实际当前状态索引（限制在正常流程范围内）
    const actualCurrent = Math.min(current, 4);
    
    normalNodes.forEach((node, index) => {
      const data = statusData[node.key];
      const completed = data?.completed ?? index < actualCurrent;
      const isCurrent = index === actualCurrent;
      
      nodes.push({
        key: node.key,
        label: t(node.label),
        time: data?.time,
        completed,
        isCurrent,
      });
    });

    // 取消状态：在末尾追加红色"Cancelled"节点
    if (isCancelled) {
      nodes.push({
        key: 'cancelled',
        label: t('order.statusFlow.canceled'),
        time: cancelTime,
        completed: false,
        isCurrent: true,
        isCancelled: true,
      });
    }

    // 退货状态：在末尾追加橙色"Returned"节点
    if (isReturned) {
      nodes.push({
        key: 'returned',
        label: t('order.statusFlow.returned'),
        time: returnTime,
        completed: false,
        isCurrent: true,
        isReturned: true,
      });
    }

    return nodes;
  };

  const statusNodes = buildStatusNodes();
  
  // 计算 Steps 的 current 属性（用于进度条显示）
  const appendedCount = (isCancelled ? 1 : 0) + (isReturned ? 1 : 0);
  const stepsCurrent = Math.min(
    current,
    appendedCount > 0 ? statusNodes.length - 1 - appendedCount : 4
  );

  /**
   * 格式化时间显示
   */
  const formatTime = (time?: string | number | null): string => {
    if (!time) return '';
    return dayjs(time).format('DD-MM-YYYY HH:mm:ss');
  };

  /**
   * 渲染步骤圆圈
   */
  const renderCircle = (node: OrderStatusNodeData, index: number) => {
    // 取消节点：红色圆圈 + X
    if (node.isCancelled) {
      return (
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            backgroundColor: '#ff4d4f',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '2px solid #ff4d4f',
            flexShrink: 0,
          }}
        >
          <CloseOutlined style={{ color: '#fff', fontSize: 16 }} />
        </div>
      );
    }

    // 退货节点：橙色圆圈 + 回退图标
    if (node.isReturned) {
      return (
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            backgroundColor: '#faad14',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '2px solid #faad14',
            flexShrink: 0,
          }}
        >
          <RollbackOutlined style={{ color: '#fff', fontSize: 16 }} />
        </div>
      );
    }

    if (node.completed) {
      return (
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            backgroundColor: '#1890ff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '2px solid #1890ff',
            flexShrink: 0,
          }}
        >
          <CheckOutlined style={{ color: '#fff', fontSize: 16 }} />
        </div>
      );
    }

    if (node.isCurrent) {
      return (
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            backgroundColor: '#1890ff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '2px solid #1890ff',
            color: '#fff',
            fontSize: 14,
            fontWeight: 'bold',
            flexShrink: 0,
          }}
        >
          {index + 1}
        </div>
      );
    }

    return (
      <div
        style={{
          width: 32,
          height: 32,
          borderRadius: '50%',
          backgroundColor: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '2px solid #d9d9d9',
          color: '#999',
          fontSize: 14,
          flexShrink: 0,
        }}
      >
        {index + 1}
      </div>
    );
  };

  /**
   * 渲染连接线
   */
  const renderLine = (index: number) => {
    if (index >= statusNodes.length - 1) return null;
    
    const isActive = index < stepsCurrent;
    // 连接到取消节点的线用红色，退货节点用橙色
    const nextIsCancelled = statusNodes[index + 1]?.isCancelled;
    const nextIsReturned = statusNodes[index + 1]?.isReturned;
    
    return (
      <div
        style={{
          flex: 1,
          height: 2,
          backgroundColor: nextIsCancelled ? '#ff4d4f' : nextIsReturned ? '#faad14' : (isActive ? '#1890ff' : '#e8e8e8'),
          margin: '0 8px',
          marginTop: 15,
        }}
      />
    );
  };

  return (
    <div
      style={{
        backgroundColor: '#fff',
        padding: '24px 16px',
        marginBottom: 24,
        borderRadius: 8,
        border: '1px solid #f0f0f0',
      }}
    >
      {/* 第一行：圆圈和连接线 */}
      <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 8 }}>
        {statusNodes.map((node, index) => (
          <React.Fragment key={node.key}>
            {renderCircle(node, index)}
            {renderLine(index)}
          </React.Fragment>
        ))}
      </div>
      
      {/* 第二行：标签和时间 */}
      <div style={{ display: 'flex' }}>
        {statusNodes.map((node, index) => (
          <div
            key={`${node.key}-label`}
            style={{
              flex: index === statusNodes.length - 1 ? 0 : 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
            }}
          >
            <Tooltip title={node.time ? formatTime(node.time) : ''}>
              <span
                style={{
                  fontSize: 13,
                  color: node.isCancelled ? '#ff4d4f' : node.isReturned ? '#faad14' : (node.completed || node.isCurrent ? '#262626' : '#999'),
                  fontWeight: node.isCurrent ? 600 : 400,
                  whiteSpace: 'nowrap',
                  marginLeft: -10,
                }}
              >
                {node.label}
              </span>
            </Tooltip>
            {node.time && (
              <span
                style={{
                  fontSize: 11,
                  color: '#999',
                  whiteSpace: 'nowrap',
                  marginLeft: -10,
                  marginTop: 4,
                }}
              >
                {formatTime(node.time)}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default OrderStatusFlow;
